// auth-context.tsx
import { createContext, useContext, useState, useEffect } from 'react';
import type React from 'react';
import type { AccessorSignIn, AccessorSignUp } from '@/accessor.types';
import type { Accessor } from '@prisma/client';

interface AuthContextType {
  isAuthenticated: boolean;
  authLevel: 'YENTA' | 'MANAGER' | 'AGENT' | 'FREE' | null;
  signin: (data: AccessorSignIn) => Promise<string>;
  /** @deprecated this isnt really used anymore, however it is still relevant */
  signup: (data: AccessorSignUp) => Promise<string>;
  getAccessor: (forceTestCheck?: boolean) => Promise<Accessor>;
  updateAccessor: (data: Partial<Accessor>) => Promise<void>;
  accessorCan: (entitlementKey: string) => boolean;
  signout: () => void;
  injectHeader: (req: Request) => void;
  accessor: Accessor | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLevel, setAuthLevel] = useState<'YENTA' | 'MANAGER' | 'AGENT' | 'FREE' | null>(null);
  const [accessor, setAccessor] = useState<Accessor | null>(null); // TODO: rename this to accessor
  const [token, setToken] = useState<string | null>(null);
  const endpointPath = '/api/entry';

  useEffect(() => {
    // Replace this with your actual authentication check logic
    const tkn = localStorage.getItem('authToken');
    const prof = localStorage.getItem('accessorProfile');
    const authLvl = localStorage.getItem('authLevel');
    setToken(tkn);
    setIsAuthenticated(!!tkn);
    console.log('prof', typeof prof);
    setAuthLevel(authLvl as 'YENTA' | 'MANAGER' | 'AGENT' | 'FREE' | null);
    if(prof === 'undefined') setAccessor(null);
    else setAccessor(JSON.parse(prof || '{}'));
  }, []);

  useEffect(()=> {
    if(token) {
      getAccessor().then(aa => {
        console.log('accessor gotten', aa);
      })
    }
  }, [token])

  const signin = async (data: AccessorSignIn) => {
    const res = await fetch(endpointPath, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if(!res.ok) {
      const errbody = await res.json() as { error: string };
      throw Error(errbody.error);
    }
    const body = await res.json() as { token: string, aid: string, accessType: string };
    localStorage.setItem('authToken', body.token);
    localStorage.setItem('authLevel', body.accessType);
    setToken(body.token);
    setIsAuthenticated(true);
    setAuthLevel(body.accessType as 'YENTA' | 'MANAGER' | 'AGENT' | 'FREE');
    return body.aid;
  };

  const signup = async (data: AccessorSignUp) => {
    const res = await fetch(endpointPath, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if(!res.ok) {
      const errbody = await res.json() as { error: string };
      throw Error(errbody.error);
    }
    const body = await res.json() as { token: string, aid: string, accessType: string };
    localStorage.setItem('authToken', body.token);
    localStorage.setItem('authLevel', body.accessType);
    setToken(body.token);
    setIsAuthenticated(true);
    setAuthLevel(body.accessType as 'YENTA' | 'MANAGER' | 'AGENT' | 'FREE');
    return body.aid;
  }

  const getAccessor = async (forceTestCheck: boolean = false) => {
    const req = new Request(`/api/auth${forceTestCheck ? '?testCheck=true' : ''}`)
    injectHeader(req);
    const res = await fetch(req);
    if(res.status === 401) {
      signout();
    } else if(!res.ok) {
      throw Error('Failed to get accessor');
    }
    const body = await res.json() as {accessor: Accessor};
    localStorage.setItem('accessorProfile', JSON.stringify(body.accessor));
    setAccessor(body.accessor);
    localStorage.setItem('accessorSubEndsAt', `${body.accessor.subEndsAt}`);
    localStorage.setItem('authLevel', body.accessor.accessType);
    return body.accessor;
  }

  const signout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('accessorProfile');
    localStorage.removeItem('authLevel');
    setToken(null);
    setAccessor(null);
    setIsAuthenticated(false);
    setAuthLevel(null);
  };

  const updateAccessor = async (data: Partial<Accessor>) => {
    const req = new Request('/api/auth', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    injectHeader(req);
    const res = await fetch(req);
    if(!res.ok) {
      throw Error('Failed to update accessor');
    }
    const body = await res.json() as { accessor: Accessor };
    setAccessor(body.accessor);
  }

  const injectHeader = (req: Request) => {
    if (!!token) {
      req.headers.set('Authorization', `Bearer ${token}`);
    } else {
      throw Error('Attempt to inject header with no token');
    }
  }

  /**
   * Checks if the accessor has the given (Stripe) entitlement key
   * @param entitlementKey - expects an exact match
   * @returns true if the accessor has the given entitlement key, false otherwise
   * @todo - create a enum for the entitlement keys
   */
  const accessorCan = (entitlementKey: string) => {
    if(!accessor || !accessor.stripeEntitlements) {
      return false;
    }
    return accessor.stripeEntitlements.split(',').includes(entitlementKey);
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, authLevel, signin, signup, signout, injectHeader, accessor, getAccessor, updateAccessor, accessorCan }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export type { AuthContextType };
export { AuthContext };
